import Button from "@amzn/meridian/esm/components/button";
import Row from "@amzn/meridian/esm/components/row";
import Tag from "@amzn/meridian/esm/components/tag";
import Theme from "@amzn/meridian/esm/components/theme";

// Chip geometry, in px: collapse the Button onto the Tag it wraps so the click target hugs the
// pill. Radius/height match the Tag's own shape; font size matches the b100 org text.
const CHIP_BORDER_RADIUS = 13;
const CHIP_HEIGHT = 20;
const CHIP_FONT_SIZE = 12;

/**
 * Org filter chips for the Who's Who panel: an "All" chip plus one per org. Each chip is a
 * Meridian Button wrapping a Tag, sharing the Tag's geometry so the click target hugs the pill.
 *
 * @param {Object} params
 * @param {string[]} params.orgs - Sorted list of org names to render chips for.
 * @param {Object<string,string>} params.orgColorMap - Maps each org to its HSL accent colour,
 *   the same colour used by the matching InviteeCard bar.
 * @param {string|null} params.activeOrg - Currently selected org, or null for "All".
 * @param {(org: string|null) => void} params.onSelect - Called with the org to filter to, or null.
 */
const WhosWhoOrgFilter = ({ orgs, orgColorMap, activeOrg, onSelect }) => {
  if (!orgs || orgs.length === 0) return null;

  return (
    <Row spacing="200" wrap="down" alignmentVertical="center">
      {/* Shared geometry for every pill: no button padding so the Tag's own inset defines the
          width; radius/height/font come from the CHIP_* constants above. */}
      <Theme
        tokens={{
          buttonBorderRadius: CHIP_BORDER_RADIUS,
          buttonHeightSmall: CHIP_HEIGHT,
          buttonSpacingInsetHorizontalSmall: 0,
          tagLabelFontSize: CHIP_FONT_SIZE,
          tagFontSize: CHIP_FONT_SIZE,
        }}
      >
        <Button
          type="link"
          aria-label="Show all organizations"
          aria-pressed={activeOrg === null}
          onClick={() => onSelect(null)}
          size="small"
        >
          {/* "All" maps to no org, so drop the indicator dot and its trailing gap. */}
          <Theme tokens={{ tagIndicatorDiameter: 0 }}>
            <Tag type="neutral">All</Tag>
          </Theme>
        </Button>
        {orgs.map((org) => (
          <Button
            key={org}
            type="link"
            aria-label={org}
            aria-pressed={activeOrg === org}
            onClick={() => onSelect(activeOrg === org ? null : org)}
            size="small"
          >
            {/* Recolor the neutral Tag's built-in indicator (and its border, which shares
                the token) to this org's HSL accent — the same colour as the matching
                InviteeCard bar, with no 5-type limit. */}
            <Theme tokens={{ tagBorderColorStatusNeutral: orgColorMap[org] }}>
              <Tag type="neutral">{org}</Tag>
            </Theme>
          </Button>
        ))}
      </Theme>
    </Row>
  );
};

export default WhosWhoOrgFilter;
